// Filename: articlePagination.ts
import fadin from "fadin";

interface ArticleRequestModel {
  FilterIds: string[];
  sort: string;
  currentPage: string;
  itemsPerPage: string;
}

interface PaginationData {
  currentPage: string;
  totalPages: string;
}

document.addEventListener("DOMContentLoaded", () => {
  updatePagination();

  fadin(".fade");

  // Event delegation for filter checkboxes, sort buttons, and pagination buttons
  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;

    // Handle filter buttons
    if (target.classList.contains("filter-button")) {
      event.preventDefault();
      const filterId = target.getAttribute("data-filter-id");
      if (filterId !== null) {
        selectFilter(event, Number(filterId));
      }
    }

    // Handle sort buttons
    if (target.classList.contains("sort-button")) {
      const sortOrder = target.getAttribute("data-sort-order");
      if (sortOrder !== null) {
        changeSortOrder(Number(sortOrder));
      }
    }

    // Handle pagination buttons
    if (target.classList.contains("pagination-button")) {
      event.preventDefault();
      const pageNumber = target.getAttribute("data-page-number");
      if (pageNumber !== null) {
        goToArticlePage(Number(pageNumber));
      }
    }
  });

  // Event delegation for filter checkboxes
  document.addEventListener("change", (event) => {
    const target = event.target as HTMLElement;
    if (target.classList.contains("article-filter-input")) {
      const filterId = target.getAttribute("data-id");
      if (filterId !== null) {
        selectFilter(event, Number(filterId));
      }
    }
  });
});

function fetchPage(): void {
  // Get element references
  const dataTagsElement = document.getElementById("data-tags");
  const dataSortElement = document.getElementById("data-sort");
  const dataPageElement = document.getElementById("data-page");
  const dataItemsPerPageElement = document.getElementById("data-items-per-page");
  const articleLoadingElement = document.getElementById("article-loading");
  const filtersRowElement = document.getElementById("filters-row"); // Add your filters row element here

  if (
    !dataTagsElement ||
    !dataSortElement ||
    !dataPageElement ||
    !dataItemsPerPageElement ||
    !articleLoadingElement ||
    !filtersRowElement // Ensure this element exists
  ) {
    console.error("One or more elements not found");
    return;
  }

  // Fetch data from the DOM
  const dataTagsAttr = dataTagsElement.getAttribute("data-tags");
  const dataTags = dataTagsAttr ? dataTagsAttr.split(",") : [];
  const dataSort = dataSortElement.getAttribute("data-sort") || "";
  const dataPage = dataPageElement.getAttribute("data-page") || "1";
  const dataItemsPerPage = dataItemsPerPageElement.getAttribute("data-items-per-page") || "10";

  // Create a model
  const articleRequestModel: ArticleRequestModel = {
    FilterIds: dataTags,
    sort: dataSort,
    currentPage: dataPage,
    itemsPerPage: dataItemsPerPage,
  };

  const articlesContainerElement = document.querySelector(".articles-container") as HTMLElement | null;
  const articlesMainContainerElement = document.querySelector(".articles-main-container") as HTMLElement | null;
  if (!articlesContainerElement || !articlesMainContainerElement) {
    console.error("Articles container elements not found");
    return;
  }

  const ensureHeight = () => {
    const currentHeight = articlesContainerElement.offsetHeight;
    if (currentHeight > 0) {
      articlesMainContainerElement.style.height = `${currentHeight}px`;
    }
  };

  ensureHeight();

  // Hide current articles before loading new ones
  document.querySelectorAll(".article-items").forEach((element) => {
    if (element instanceof HTMLElement) {
      element.style.display = "none";
    }
  });

  articleLoadingElement.style.display = "flex"; // Show loading spinner

  setTimeout(() => {
    fetch("/umbraco/surface/ArticleSurface/GetBlogArticlesForPage", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(articleRequestModel),
    })
      .then((response) => response.text())
      .then((data) => {
        if (data !== "") {
          articlesContainerElement.innerHTML = data;

          const currentState = history.state;
          const queryParameters = new URLSearchParams(window.location.search);

          queryParameters.set("pg", articleRequestModel.currentPage);
          queryParameters.set("s", articleRequestModel.sort);
          queryParameters.set("f", articleRequestModel.FilterIds.join(","));

          const newUrl = `${window.location.origin}${window.location.pathname}?${queryParameters.toString()}`;
          window.history.pushState(currentState, "", newUrl);

          // Re-initialize fadin after content has been replaced
          const fadeInInstance = fadin(".fade", {
            noInitalScrollEvent: true, // Prevent waiting for the scroll event
            delay: 200, // Optional: Adjust the delay as needed
          });

          fadeInInstance.reset(); // Manually trigger the animation by resetting (also calls sendScroll)

          window.dispatchEvent(new Event("scroll")); // Manually trigger a scroll event to force the animation

          updatePagination();

          // Scroll to the filters row after the content is updated
          const filtersRowTop = filtersRowElement.getBoundingClientRect().top + window.scrollY;

// Scroll 35px above the filters row
          window.scrollTo({
            top: filtersRowTop - 35,
            behavior: "smooth",
          });
        }

        // Preserve the checked states for the filters
        const inputs = document.querySelectorAll("input.article-filter-input") as NodeListOf<HTMLInputElement>;
        inputs.forEach((input) => {
          const filterId = input.getAttribute("data-id");
          if (filterId && articleRequestModel.FilterIds.includes(filterId)) {
            input.checked = true; // Recheck the filter if it's in the FilterIds array
          } else {
            input.checked = false;
          }
        });

        articleLoadingElement.style.display = "none"; // Hide the loading spinner

        setTimeout(() => {
          articlesMainContainerElement.style.height = `${articlesContainerElement.offsetHeight}px`;
          setTimeout(() => {
            articlesMainContainerElement.style.height = "auto";
          }, 200);
        }, 0);
      })
      .catch((error) => {
        console.error(error);
        articleLoadingElement.style.display = "none";
        setTimeout(() => {
          articlesMainContainerElement.style.height = "auto";
        }, 500);
      });
  }, 500); // Simulate a 1-second delay before the network call
}


function selectFilter(event: Event, itm: number): void {
  const dataPageElement = document.getElementById("data-page");
  const dataTagsElement = document.getElementById("data-tags");

  if (!dataPageElement || !dataTagsElement) {
    console.error("Necessary elements not found");
    return;
  }

  // Reset to page 1 when filtering
  dataPageElement.setAttribute("data-page", "1");

  event.stopPropagation();

  const existingTagsAttr = dataTagsElement.getAttribute("data-tags");
  const tagsArray = existingTagsAttr ? existingTagsAttr.split(",") : [];

  if (itm === 0) {
    // Clear the array for "All Posts"
    dataTagsElement.setAttribute("data-tags", "");
  } else {
    const tagIndex = tagsArray.indexOf(itm.toString());

    if (tagIndex === -1) {
      // Add item
      tagsArray.push(itm.toString());
    } else {
      // Remove item
      tagsArray.splice(tagIndex, 1);
    }

    // Update the 'data-tags' attribute
    dataTagsElement.setAttribute("data-tags", tagsArray.join(","));
  }

  console.log("Updated tagsArray:", tagsArray);

  fetchPage(); // This will now update both the page and pagination
}

function changeSortOrder(sortOrder: number): void {
  const dataSort = document.getElementById("data-sort");

  if (!dataSort) {
    console.error("data-sort element not found");
    return;
  }

  dataSort.setAttribute("data-sort", sortOrder.toString());

  fetchPage();
}

function goToArticlePage(page: number): void {
  const dataPageElement = document.getElementById("data-page");

  if (!dataPageElement) {
    console.error("data-page element not found");
    return;
  }

  dataPageElement.setAttribute("data-page", page.toString());

  fetchPage();
}

// Debounce function to limit the frequency of pagination updates during resizing
function debounce(func: () => void, wait: number) {
  let timeout: number | undefined;
  return function () {
    const later = () => {
      clearTimeout(timeout);
      func();
    };
    clearTimeout(timeout);
    timeout = window.setTimeout(later, wait);
  };
}

function updatePagination(): void {
  let currentPage = 1;
  let totalPages = 1;

  const dataTagsElement = document.getElementById("data-tags");
  const dataSortElement = document.getElementById("data-sort");
  const dataPageElement = document.getElementById("data-page");
  const dataItemsPerPageElement = document.getElementById("data-items-per-page");

  if (
    !dataTagsElement ||
    !dataSortElement ||
    !dataPageElement ||
    !dataItemsPerPageElement
  ) {
    console.error("One or more elements not found");
    return;
  }

  // Fetch data from the DOM
  const dataTagsAttr = dataTagsElement.getAttribute("data-tags");
  const dataTags = dataTagsAttr ? dataTagsAttr.split(",") : [];
  const dataSort = dataSortElement.getAttribute("data-sort") || "";
  const dataPage = dataPageElement.getAttribute("data-page") || "1";
  const dataItemsPerPage = dataItemsPerPageElement.getAttribute("data-items-per-page") || "10";

  const articleRequestModel: ArticleRequestModel = {
    FilterIds: dataTags,
    sort: dataSort,
    currentPage: dataPage,
    itemsPerPage: dataItemsPerPage,
  };

  fetch("/umbraco/surface/ArticleSurface/GetBlogPaginationForPage", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(articleRequestModel),
  })
    .then((response) => response.json())
    .then((data: PaginationData) => {
      if (data !== null) {
        currentPage = parseInt(data.currentPage, 10);
        totalPages = parseInt(data.totalPages, 10);
      }

      const paginationContainer = document.querySelector(".pagination") as HTMLElement | null;
      if (!paginationContainer) {
        console.error("Pagination container not found");
        return;
      }
      paginationContainer.innerHTML = "";

      // Helper function to create pagination buttons
      const createPaginationButton = (pageNumber: number, text: string, isActive = false) => {
        const li = document.createElement("li");
        li.className = `page-item ${isActive ? "active" : ""}`;
        const button = document.createElement("button");
        button.className = "page-link pagination-button";
        button.setAttribute("data-page-number", pageNumber.toString());
        button.setAttribute("data-text", text); // Add the data-text attribute
        button.textContent = text;
        li.appendChild(button);
        paginationContainer.appendChild(li);
      };

      // Mobile check: If screen width is less than or equal to 768px
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // Show "Previous" if not on the first page
        if (currentPage > 1) {
          createPaginationButton(currentPage - 1, "Previous");
        }

        // Handle the three-page logic
        if (currentPage === 1) {
          // If on the first page, show pages 1, 2, and 3
          for (let i = 1; i <= Math.min(3, totalPages); i++) {
            createPaginationButton(i, i.toString(), currentPage === i);
          }
        } else if (currentPage === totalPages) {
          // If on the last page, show the last three pages
          for (let i = Math.max(totalPages - 2, 1); i <= totalPages; i++) {
            createPaginationButton(i, i.toString(), currentPage === i);
          }
        } else {
          // For middle pages, show the previous, current, and next pages
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            createPaginationButton(i, i.toString(), currentPage === i);
          }
        }

        // Show "Next" if not on the last page
        if (currentPage < totalPages) {
          createPaginationButton(currentPage + 1, "Next");
        }

      } else {
        // Full pagination for desktop, including "First" and "Last" buttons
        const startingPage = Math.max(1, currentPage - 5);
        const lastPage = Math.min(totalPages, currentPage + 5);

        // First Page Button
        if (currentPage !== 1) {
          createPaginationButton(1, "First");
        }

        // Previous Page Button
        if (currentPage > 1) {
          createPaginationButton(currentPage - 1, "Previous");
        }

        // Page Number Buttons
        for (let i = startingPage; i <= lastPage; i++) {
          createPaginationButton(i, i.toString(), currentPage === i);
        }

        // Next Page Button
        if (currentPage < totalPages) {
          createPaginationButton(currentPage + 1, "Next");
        }

        // Last Page Button
        if (currentPage !== totalPages) {
          createPaginationButton(totalPages, "Last");
        }
      }
    })
    .catch((error) => {
      console.error(error);
    });
}

// Trigger pagination update on window resize with a debounce of 200ms
window.addEventListener("resize", debounce(updatePagination, 200));

// Call updatePagination on page load
document.addEventListener("DOMContentLoaded", () => {
  updatePagination();
});

// Function to fade in elements when they come into view
function fadeInOnScroll() {
  const fadeElements = document.querySelectorAll(".fade");

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add("fade-in");
        observer.unobserve(entry.target);  // Stop observing once faded in
      }
    });
  }, { threshold: 0.1 });  // Trigger when 10% of the element is visible

  fadeElements.forEach(element => {
    observer.observe(element);
  });
}

// Function to observe DOM changes and trigger animations when content is updated
function observeDomChanges() {
  const targetNode = document.querySelector(".articles-container"); // Replace with your target container
  if (!targetNode) return;  // In case the element isn't present on the page

  const config = { childList: true, subtree: true };

  // Callback function to execute when mutations are observed
  const callback = () => {
    fadeInOnScroll();  // Trigger fade-in whenever the content changes
  };

  // Create an observer instance linked to the callback function
  const mutationObserver = new MutationObserver(callback);

  // Start observing the target node for configured mutations
  mutationObserver.observe(targetNode, config);
}

// Call fadeInOnScroll on page load and set up mutation observer
document.addEventListener("DOMContentLoaded", () => {
  fadeInOnScroll();  // Check for visible elements immediately
  observeDomChanges();  // Set up observer to track DOM changes
});
